














































































































































import { Component, Ref, Vue } from "vue-property-decorator";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import MiraInsurerDialogComponent from "@/components/admin/mira/MiraInsurerDialog.vue";
import MiraInsurerMoveDialogComponent from "@/components/admin/mira/MiraInsurerMoveDialog.vue";
import MiraInsurerMergeDialogComponent from "@/components/admin/mira/MiraInsurerMergeDialog.vue";
import { MiraInsurer } from "@/data/models/MiraInsurerAdministration";
import { listMiraInsurers } from "@/data/repository/MiraInsurerAdministrationRepository";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { getValue, setValue } from "@/utils/SecureLocalStorage";
import { getLastVisitedItem, LAST_VISITED_ITEM_BACKGROUND_STYLE, setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import { filterInsurers, MIRA_INSURER_FILTERS, MiraInsurerFilter } from "@/utils/MiraInsurerAdministration";
import { themed } from "@/utils/Theme";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/**
 * Mira's insurer list in the IRES Admin Client (Track #2610040 P3): MDIS's insurers and those IRES added,
 * with the spellings and words that resolve a typed insurer name to them. A row opens the insurer to view
 * and edit; the round plus adds one; an active row's menu moves its spellings or merges it into another
 * (#2610125). An edit reaches Mira with the next nightly run.
 *
 * **Not the access boundary**: `listMiraInsurersForAdministration` repeats the portal and `MANAGE_CLIENTS`
 * checks, as every save does.
 */
const DEFAULT_FILTER: MiraInsurerFilter = "ACTIVE";
/** Session storage key (see `SecureLocalStorage`) for the list's filter. */
const FILTER_STORAGE_KEY = "ADMIN_MIRA_INSURERS_FILTER";

@Component({
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "content-state": ContentStateComponent,
    "insurer-dialog": MiraInsurerDialogComponent,
    "move-dialog": MiraInsurerMoveDialogComponent,
    "merge-dialog": MiraInsurerMergeDialogComponent,
  },
})
export default class MiraInsurerListComponent extends Vue {
  @Ref("insurerDialog")
  insurerDialog!: MiraInsurerDialogComponent;

  @Ref("moveDialog")
  moveDialog!: MiraInsurerMoveDialogComponent;

  @Ref("mergeDialog")
  mergeDialog!: MiraInsurerMergeDialogComponent;

  insurers: MiraInsurer[] = [];
  filter: MiraInsurerFilter = DEFAULT_FILTER;
  search = "";
  isLoading = true;
  loadError = false;
  lastVisitedKey: string | null = null;

  readonly filters = MIRA_INSURER_FILTERS;
  readonly defaultFilter = DEFAULT_FILTER;

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  get rows(): MiraInsurer[] {
    return filterInsurers(this.insurers, this.filter, this.search);
  }

  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(row: MiraInsurer, hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    if (background) {
      return `background-color: ${background}`;
    }
    return row.key === this.lastVisitedKey ? LAST_VISITED_ITEM_BACKGROUND_STYLE : "";
  }

  filterName(filter: MiraInsurerFilter): string {
    return this.$t(`admin.miraInsurers.filters.${filter}`).toString();
  }

  /** The extra spellings and words; the key and names resolve too but are not counted. */
  spellingCount(row: MiraInsurer): number {
    return row.spellings.length + row.words.length;
  }

  /** The short name of the insurer a merged one went into, or its id when it is not in the list. */
  mergedIntoName(row: MiraInsurer): string {
    const target = this.insurers.find((insurer) => insurer.key === row.mergedInto);
    return target ? `${target.shortName} (${target.code})` : row.mergedInto ?? "";
  }

  onFilterChanged(filter: MiraInsurerFilter) {
    this.filter = filter;
    setValue(FILTER_STORAGE_KEY, filter);
  }

  onSearch(value: string | null) {
    this.search = value ?? "";
  }

  async open(row: MiraInsurer | null) {
    if (row) {
      this.lastVisitedKey = row.key;
      setLastVisitedItem(row.key, StorageKeyLastVisitedItemLevel.LEVEL_1);
    }
    const result = await this.insurerDialog.show(row);
    if (result === "CHANGED_MEANWHILE") {
      this.refreshInsurers();
    } else if (result) {
      this.lastVisitedKey = result.key;
      setLastVisitedItem(result.key, StorageKeyLastVisitedItemLevel.LEVEL_1);
      const index = this.insurers.findIndex((insurer) => insurer.key === result.key);
      this.insurers = index === -1 ? [...this.insurers, result] : this.insurers.map((insurer) => (insurer.key === result.key ? result : insurer));
    }
  }

  /** Schreibweisen verschieben (#2610125): source and target change, so the list is read again in place. */
  async moveSpellings(row: MiraInsurer) {
    this.markVisited(row);
    if (await this.moveDialog.show(row, this.insurers)) this.refreshInsurers();
  }

  /** Zusammenführen (#2610125): both insurers change, so the list is read again in place. */
  async merge(row: MiraInsurer) {
    this.markVisited(row);
    if (await this.mergeDialog.show(row, this.insurers)) this.refreshInsurers();
  }

  private markVisited(row: MiraInsurer) {
    this.lastVisitedKey = row.key;
    setLastVisitedItem(row.key, StorageKeyLastVisitedItemLevel.LEVEL_1);
  }

  /**
   * After a move, a merge or a refusal as changed meanwhile: reads the insurers again in the background, without the page's
   * loading state, so the list stays where it is (Taimoor, 2026-10-20: no full reload after a save).
   */
  async refreshInsurers() {
    try {
      this.insurers = (await listMiraInsurers()).insurers;
    } catch {
      // Swallowed on purpose: the list keeps what it showed, and the refresh button reloads on demand.
    }
  }

  async load() {
    this.isLoading = true;
    this.loadError = false;
    try {
      this.insurers = (await listMiraInsurers()).insurers;
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and the refresh button retries.
      this.loadError = true;
    } finally {
      this.isLoading = false;
    }
  }

  mounted() {
    ToolbarContentModule.setI18nResource("admin.miraInsurers.title");
    const stored = getValue(FILTER_STORAGE_KEY);
    this.filter = MIRA_INSURER_FILTERS.find((filter) => filter === stored) ?? DEFAULT_FILTER;
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.load();
  }
}
